import React, { useState, useEffect, useRef } from 'react';
import {
    getServices, createService, updateService, deleteService,
    getCategories, createCategory, updateCategory, deleteCategory
} from '../services/pocketbase';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiSettings, FiMove, FiMoreVertical, FiRefreshCw, FiGrid, FiList } from 'react-icons/fi';
import './AdminServices.css';

function AdminServices() {
    // Data States
    const [services, setServices] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [setupNeeded, setSetupNeeded] = useState(false);

    // UI States
    const [activeTab, setActiveTab] = useState('all'); // 'all' or category ID
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [isEditingService, setIsEditingService] = useState(false);
    const [isManagingCategories, setIsManagingCategories] = useState(false);

    // Sort State
    const [isSorting, setIsSorting] = useState(false); // For indicator
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);
    const dragGroupId = useRef(null); // Pour sécuriser le drag entre groupes

    // Forms
    const [serviceForm, setServiceForm] = useState({
        title: '',
        duration: 30,
        price: 0,
        active: true,
        category: '',
        description: '',
        on_quote: false
    });
    const [editingServiceId, setEditingServiceId] = useState(null);

    // Category Form (inside manager)
    const [newCatName, setNewCatName] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [cats, servs] = await Promise.all([getCategories(), getServices()]);
            setCategories(cats);
            setServices(servs);

            if (cats.length === 0 && servs.length === 0) {
                // Check setup
            }

        } catch (error) {
            console.error("Erreur chargement données", error);
            setSetupNeeded(true);
        } finally {
            setLoading(false);
        }
    };

    // --- SERVICE MANAGEMENT ---

    const handleEditService = (service) => {
        setEditingServiceId(service.id);
        setServiceForm({
            title: service.title,
            duration: service.duration,
            price: service.price,
            active: service.active,
            category: service.category || '',
            description: service.description || '',
            on_quote: service.on_quote || false
        });
        setIsEditingService(true);
    };

    const handleCreateService = () => {
        setEditingServiceId(null);
        setServiceForm({
            title: '',
            duration: 30,
            price: 0,
            active: true,
            category: activeTab !== 'all' ? activeTab : (categories[0]?.id || ''),
            description: '',
            on_quote: false
        });
        setIsEditingService(true);
    };

    const handleServiceSubmit = async (e) => {
        e.preventDefault();
        try {
            let order = 0;
            if (!editingServiceId) {
                const currentCatServices = services.filter(s => s.category === serviceForm.category);
                order = currentCatServices.length + 1;
            }

            const payload = { ...serviceForm };
            if (!editingServiceId) payload.order = order;
            // Force price 0 if on quote
            if (payload.on_quote) payload.price = 0;

            if (editingServiceId) {
                await updateService(editingServiceId, payload);
            } else {
                await createService(payload);
            }
            setIsEditingService(false);
            loadData();
        } catch (error) {
            alert('Erreur lors de l\'enregistrement. Vérifiez que la collection "services" a bien les champs requis.');
            console.error(error);
        }
    };

    const handleDeleteService = async (id) => {
        if (window.confirm('Supprimer ce service ?')) {
            try {
                await deleteService(id);
                setServices(prev => prev.filter(s => s.id !== id));
            } catch (error) {
                alert('Erreur suppression');
            }
        }
    };

    // --- CATEGORY MANAGEMENT ---

    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (!newCatName.trim()) return;
        try {
            const maxOrder = categories.length + 1;
            const newCat = await createCategory({
                name: newCatName,
                order: maxOrder
            });
            setCategories([...categories, newCat]);
            setNewCatName('');
        } catch (error) {
            alert("Erreur création catégorie. Vérifiez que la collection 'categories' existe.");
        }
    };

    const handleDeleteCategory = async (id) => {
        if (window.confirm('Supprimer cette catégorie ? Les services associés perdront leur catégorie.')) {
            try {
                await deleteCategory(id);
                setCategories(prev => prev.filter(c => c.id !== id));
                if (activeTab === id) setActiveTab(categories[0]?.id || 'all');
            } catch (error) {
                alert('Erreur suppression');
            }
        }
    };

    // --- DRAG AND DROP (Services & Categories) ---

    const handleDragStart = (e, index, type = 'service', groupId = null) => {
        dragItem.current = index;
        dragGroupId.current = groupId;
        const target = e.target.closest(type === 'service' ? '.service-card' : '.category-item');
        if (target) target.classList.add('dragging');
    };

    const handleDragEnter = (e, index, itemList, setItemList, type = 'service', groupId = null) => {
        // Optimisation
        if (dragItem.current === index) return;

        // Sécurité Drag Group
        if (type === 'service' && groupId && dragGroupId.current && dragGroupId.current !== groupId) {
            return;
        }

        const newList = [...itemList];
        const draggedItemContent = newList[dragItem.current];

        if (!draggedItemContent) return;

        newList.splice(dragItem.current, 1);
        newList.splice(dragOverItem.current, 0, draggedItemContent);

        dragItem.current = dragOverItem.current;
        setItemList(newList);
        dragOverItem.current = index;
    };

    const handleDragEndService = async (e) => {
        const target = e.target.closest('.service-card');
        if (target) target.classList.remove('dragging');

        const currentGroupId = dragGroupId.current || (activeTab !== 'all' ? activeTab : null);
        dragItem.current = null;
        dragOverItem.current = null;
        dragGroupId.current = null;

        if (!currentGroupId) return;

        // On filtre 'services' (le state global à jour) pour ne garder que ceux du groupe affecté
        let groupServicesToSave = [];
        if (currentGroupId === 'uncategorized') {
            groupServicesToSave = services.filter(s => !s.category);
        } else {
            // C'est un ID de catégorie
            groupServicesToSave = services.filter(s => s.category === currentGroupId);
        }

        // Fallback safety
        if (groupServicesToSave.length === 0 && activeTab !== 'all' && activeTab !== 'uncategorized') {
            groupServicesToSave = services.filter(s => s.category === activeTab);
        }

        if (groupServicesToSave.length === 0) return;

        try {
            setIsSorting(true);
            // On attribue des index 1, 2, 3... basés sur leur position actuelle visuelle
            await Promise.all(groupServicesToSave.map((s, idx) => updateService(s.id, { order: idx + 1 })));
        } catch (err) { console.error(err); }
        finally { setIsSorting(false); }
    };

    const handleDragEndCategory = async (e) => {
        const target = e.target.closest('.category-item');
        if (target) target.classList.remove('dragging');
        try {
            setIsSorting(true);
            await Promise.all(categories.map((c, idx) => updateCategory(c.id, { order: idx + 1 })));
        } catch (err) { console.error(err); }
        finally { setIsSorting(false); }
    };

    // --- RENDER HELPERS ---

    const getFilteredServices = () => {
        const catOrderMap = categories.reduce((acc, cat) => ({ ...acc, [cat.id]: cat.order || 999 }), {});
        let filtered = services;

        if (activeTab !== 'all') {
            filtered = services.filter(s => s.category === activeTab || (activeTab === 'uncategorized' && !s.category));
        }

        return filtered.sort((a, b) => {
            if (activeTab === 'all') {
                const catA = catOrderMap[a.category] || 9999;
                const catB = catOrderMap[b.category] || 9999;
                if (catA !== catB) return catA - catB;
            }
            return (a.order || 0) - (b.order || 0);
        });
    };

    const updateLocalOrderedServices = (newOrderedList) => {
        // En mode 'all' ou standard, on doit mettre à jour les propriétés 'order' des objets en mémoire
        // pour que le tri .sort() du render ne vienne pas annuler le drag & drop visuel.
        // On assigne un ordre provisoire (juste pour le visuel, la DB sera update au drop)
        const updatedSubList = newOrderedList.map((item, index) => ({
            ...item,
            order: index + 1
        }));

        const modifiedIds = new Set(updatedSubList.map(s => s.id));

        setServices(prev => {
            const others = prev.filter(s => !modifiedIds.has(s.id));
            return [...others, ...updatedSubList];
        });
    };

    if (loading) return <div className="loader">Chargement...</div>;

    return (
        <div className="admin-services">
            <div className="admin-header">
                <h2>💇‍♀️ Gestion des Tarifs & Services</h2>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div className="view-toggles">
                        <button
                            className={`btn-icon ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => setViewMode('grid')}
                            title="Vue Grille"
                        >
                            <FiGrid />
                        </button>
                        <button
                            className={`btn-icon ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => setViewMode('list')}
                            title="Vue Liste"
                        >
                            <FiList />
                        </button>
                    </div>
                    <button className="btn-manage-cats" onClick={() => setIsManagingCategories(true)}>
                        <FiSettings /> Gérer les Catégories
                    </button>
                    <button className="btn-add" onClick={handleCreateService}>
                        <FiPlus /> Ajouter un service
                    </button>
                </div>
            </div>

            {setupNeeded && (
                <div className="setup-alert">
                    <h4>⚠️ Configuration Base de Données Requise</h4>
                    <p>Pour utiliser les catégories, créez la collection <code>categories</code>...</p>
                    <button onClick={loadData}>J'ai configuré, rafraîchir</button>
                </div>
            )}

            {/* TABS */}
            <div className="tabs-container">
                <button
                    className={`tab ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                >
                    Tous
                </button>
                <button
                    className={`tab ${activeTab === 'uncategorized' ? 'active' : ''}`}
                    onClick={() => setActiveTab('uncategorized')}
                >
                    Sans catégorie
                </button>
                {categories.map(cat => (
                    <button
                        key={cat.id}
                        className={`tab ${activeTab === cat.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(cat.id)}
                    >
                        {cat.name}
                    </button>
                ))}
            </div>

            {/* SERVICES LIST */}
            {activeTab === 'all' ? (
                <div className="services-all-view">
                    {categories.map(cat => {
                        const catServices = services.filter(s => s.category === cat.id).sort((a, b) => (a.order || 0) - (b.order || 0));
                        if (catServices.length === 0) return null;

                        return (
                            <div key={cat.id} className="category-group-section">
                                <h3 style={{ margin: '2rem 0 1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #eee', color: '#666' }}>{cat.name}</h3>
                                <div className={`services-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
                                    {catServices.map((service, index) => (
                                        <div
                                            key={service.id}
                                            className={`service-card ${!service.active ? 'inactive' : ''} draggable`}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index, 'service', cat.id)}
                                            onDragEnter={(e) => handleDragEnter(e, index, catServices, updateLocalOrderedServices, 'service', cat.id)}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDragEnd={handleDragEndService}
                                        >
                                            <div className="service-header">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <FiMove className="service-drag-handle" />
                                                    <h3>{service.title}</h3>
                                                </div>
                                                <span className="price-tag">{service.on_quote ? 'Sur devis' : `${service.price} €`}</span>
                                            </div>
                                            <div className="service-body">
                                                <p className="duration">⏱ {service.duration} min</p>
                                                {service.description && <p className="description">{service.description}</p>}
                                                {!service.active && <span className="status-badge">Inactif</span>}
                                            </div>
                                            <div className="service-actions">
                                                <button onClick={() => handleEditService(service)} className="btn-icon edit"><FiEdit2 /></button>
                                                <button onClick={() => handleDeleteService(service.id)} className="btn-icon delete"><FiTrash2 /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}

                    {/* Uncategorized group in All view */}
                    {(() => {
                        const uncategorized = services.filter(s => !s.category).sort((a, b) => (a.order || 0) - (b.order || 0));
                        if (uncategorized.length === 0) return null;
                        return (
                            <div className="category-group-section">
                                <h3 style={{ margin: '2rem 0 1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #eee', color: '#666' }}>Sans catégorie</h3>
                                <div className={`services-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
                                    {uncategorized.map((service, index) => (
                                        <div
                                            key={service.id}
                                            className={`service-card ${!service.active ? 'inactive' : ''} draggable`}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index, 'service', 'uncategorized')}
                                            onDragEnter={(e) => handleDragEnter(e, index, uncategorized, updateLocalOrderedServices, 'service', 'uncategorized')}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDragEnd={handleDragEndService}
                                        >
                                            <div className="service-header">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <FiMove className="service-drag-handle" />
                                                    <h3>{service.title}</h3>
                                                </div>
                                                <span className="price-tag">{service.on_quote ? 'Sur devis' : `${service.price} €`}</span>
                                            </div>
                                            <div className="service-body">
                                                <p className="duration">⏱ {service.duration} min</p>
                                                {service.description && <p className="description">{service.description}</p>}
                                                {!service.active && <span className="status-badge">Inactif</span>}
                                            </div>
                                            <div className="service-actions">
                                                <button onClick={() => handleEditService(service)} className="btn-icon edit"><FiEdit2 /></button>
                                                <button onClick={() => handleDeleteService(service.id)} className="btn-icon delete"><FiTrash2 /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            ) : (
                <div className={`services-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
                    {getFilteredServices().length === 0 ? (
                        <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#888', padding: '2rem' }}>
                            Aucun service dans cette catégorie.
                        </div>
                    ) : (
                        getFilteredServices().map((service, index) => (
                            <div
                                key={service.id}
                                className={`service-card ${!service.active ? 'inactive' : ''} draggable`}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index, 'service', activeTab)}
                                onDragEnter={(e) => {
                                    handleDragEnter(e, index, getFilteredServices(), updateLocalOrderedServices, 'service', activeTab)
                                }}
                                onDragOver={(e) => e.preventDefault()}
                                onDragEnd={handleDragEndService}
                            >
                                <div className="service-header">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <FiMove className="service-drag-handle" />
                                        <h3>{service.title}</h3>
                                    </div>
                                    <span className="price-tag">{service.on_quote ? 'Sur devis' : `${service.price} €`}</span>
                                </div>
                                <div className="service-body">
                                    <p className="duration">⏱ {service.duration} min</p>
                                    {service.description && <p className="description">{service.description}</p>}
                                    {!service.active && <span className="status-badge">Inactif</span>}
                                </div>
                                <div className="service-actions">
                                    <button onClick={() => handleEditService(service)} className="btn-icon edit"><FiEdit2 /></button>
                                    <button onClick={() => handleDeleteService(service.id)} className="btn-icon delete"><FiTrash2 /></button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* SERVICE MODAL */}
            {isEditingService && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>{editingServiceId ? 'Modifier' : 'Nouveau Service'}</h3>
                        <form onSubmit={handleServiceSubmit}>
                            <div className="form-group">
                                <label>Catégorie</label>
                                <select
                                    value={serviceForm.category}
                                    onChange={e => setServiceForm({ ...serviceForm, category: e.target.value })}
                                >
                                    <option value="">-- Sans catégorie --</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Titre</label>
                                <input type="text" value={serviceForm.title} onChange={e => setServiceForm({ ...serviceForm, title: e.target.value })} required />
                            </div>
                            <div className="form-group">
                                <label>Description (ex: Coupe et soins inclus)</label>
                                <textarea value={serviceForm.description} onChange={e => setServiceForm({ ...serviceForm, description: e.target.value })} />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                        <label style={{ marginBottom: 0 }}>Prix (€)</label>
                                        <label style={{ fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                            <input
                                                type="checkbox"
                                                checked={serviceForm.on_quote}
                                                onChange={e => setServiceForm({ ...serviceForm, on_quote: e.target.checked, price: e.target.checked ? 0 : serviceForm.price })}
                                            />
                                            Sur devis
                                        </label>
                                    </div>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={serviceForm.price}
                                        onChange={e => setServiceForm({ ...serviceForm, price: parseFloat(e.target.value) })}
                                        disabled={serviceForm.on_quote}
                                        required={!serviceForm.on_quote}
                                        placeholder={serviceForm.on_quote ? "Non applicable" : "0.00"}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Durée (min)</label>
                                    <input type="number" value={serviceForm.duration} onChange={e => setServiceForm({ ...serviceForm, duration: parseInt(e.target.value) })} required />
                                </div>
                            </div>
                            <div className="form-group checkbox-group">
                                <label><input type="checkbox" checked={serviceForm.active} onChange={e => setServiceForm({ ...serviceForm, active: e.target.checked })} /> Actif</label>
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => setIsEditingService(false)}>Annuler</button>
                                <button type="submit" className="btn-save">Enregistrer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* CATEGORIES MANAGER MODAL */}
            {isManagingCategories && (
                <div className="modal-overlay">
                    <div className="modal-content category-manager-modal">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Gérer les Catégories</h3>
                            <button onClick={() => setIsManagingCategories(false)} className="btn-icon"><FiX /></button>
                        </div>

                        <form onSubmit={handleAddCategory} className="add-cat-form">
                            <input
                                type="text"
                                placeholder="Nouvelle catégorie (ex: Femmes)"
                                value={newCatName}
                                onChange={e => setNewCatName(e.target.value)}
                            />
                            <button type="submit" className="btn-add-small"><FiPlus /></button>
                        </form>

                        <div className="category-manager-list">
                            {categories.map((cat, index) => (
                                <div
                                    key={cat.id}
                                    className="category-item"
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index, 'category')}
                                    onDragEnter={(e) => handleDragEnter(e, index, categories, setCategories, 'category')}
                                    onDragEnd={handleDragEndCategory}
                                >
                                    <div className="category-info">
                                        <FiMove className="cat-drag-handle" />
                                        <span>{cat.name}</span>
                                    </div>
                                    <div className="category-actions">
                                        <button onClick={() => handleDeleteCategory(cat.id)} className="btn-icon delete"><FiTrash2 /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p style={{ fontSize: '0.8rem', color: '#888', textAlign: 'center' }}>
                            Glissez pour réorganiser. {isSorting && "Sauvegarde..."}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminServices;
